// @flow
import type {Node} from 'react'
import React, {useCallback} from 'react'
import {useFragment, graphql} from 'react-relay'

import liftNodes from 'utils/GraphQL/typedLiftNodes'
import Heading from 'components/ui/Heading'
import Box from 'components/OrgNav/OrgNavApp/RoleContainer/Sections/ui/Box'
import AddProposalChangeMutation from 'mutations/AddProposalChangeMutation'

import {useOrganizationContext} from 'contexts/OrganizationContext'

import RoleSelect from './RoleSelect'
import PolicySelect from './PolicySelect'
import SupportedRoleSelect from './SupportedRoleSelect'
import ElectRoleFillerSelect from './ElectRoleFillerSelect'

import type {PartSelector_meeting$key as MeetingKey} from './__generated__/PartSelector_meeting.graphql'

import type {PartSelector_proposal$key as ProposalKey} from './__generated__/PartSelector_proposal.graphql'

type Props = $ReadOnly<{
  disabled: boolean,
  meeting: ?MeetingKey,
  proposal: ProposalKey,
}>

const proposalFragment = graphql`
  fragment PartSelector_proposal on Proposal {
    id

    tension {
      body
    }

    availableSupportedRoles {
      edges {
        node {
          id
        }
      }
    }
    ...RoleSelect_proposal
    ...PolicySelect_proposal
    ...SupportedRoleSelect_proposal
    ...ElectRoleFillerSelect_proposal
  }
`

const meetingFragment = graphql`
  fragment PartSelector_meeting on Meeting {
    id

    circle {
      databaseId
      supportedRole {
        id
        isExpanded
      }
    }
  }
`

function PartSelector({
  disabled,
  meeting: meetingKey,
  proposal: proposalKey,
}: Props): Node {
  const proposal = useFragment(proposalFragment, proposalKey)
  const meeting = useFragment(meetingFragment, meetingKey)
  const subcircles = liftNodes(proposal.availableSupportedRoles)
  const hasSubcircles = subcircles.length > 0

  const addPart = useCallback(
    (command, typename) => AddProposalChangeMutation(
      {
        command,
        proposalId: proposal.id,
        meetingId: meeting?.id,
      },
      typename,
    ),
    [proposal, meeting],
  )
  const organization = useOrganizationContext()

  return (
    <Box>
      <Heading headingLevel={2} spacing="xSmall" underlined>
        {I18n.t('governance_meetings.react.add_part')}
      </Heading>
      <Box.Body topPadding bottomPadding>
        <div className="refinery-select-parts" id="add-part">
          <RoleSelect
            disabled={disabled}
            onChange={addPart}
            proposal={proposal}
            className="test-role-select"
          />
          <PolicySelect
            disabled={disabled}
            onChange={addPart}
            proposal={proposal}
            className="test-policy-select"
          />
          {hasSubcircles && organization.isOnV4 && (
            <SupportedRoleSelect
              disabled={disabled}
              onChange={addPart}
              proposal={proposal}
              className="test-restructure-circle-select"
            />
          )}
          {meeting?.circle.supportedRole?.isExpanded && (
            <ElectRoleFillerSelect
              disabled={disabled}
              onChange={addPart}
              proposal={proposal}
              className="test-elect-role-filler-select"
            />
          )}
        </div>
      </Box.Body>
    </Box>
  )
}

export default PartSelector
